/**
 * Grants API service: grant, list, and revoke capability sets on a study or DTA, and list a DTA's
 * vendor members as grantee suggestions.
 */

import type {
  CreateGrantRequest,
  VendorUserListResponse,
  GrantItem,
  GrantListResponse,
} from "@/generated/sdk"

import { sdk, sdkCall } from "./sdk"

export type { GrantItem } from "@/generated/sdk"

/**
 * The resource a grant is placed on. A study grant cascades to the study's DTAs.
 */
export type GrantTarget = { type: "study"; id: string } | { type: "dta"; id: string }

export const grantsApi = {
  createGrant: async (target: GrantTarget, request: CreateGrantRequest): Promise<GrantItem> => {
    return target.type === "study"
      ? sdkCall(sdk.createStudyGrant({ studyId: target.id, createGrantRequest: request }))
      : sdkCall(sdk.createDtaGrant({ dtaId: target.id, createGrantRequest: request }))
  },

  listGrants: async (
    target: GrantTarget,
    params: { limit?: number; nextToken?: string } = {},
  ): Promise<GrantListResponse> => {
    return target.type === "study"
      ? sdkCall(sdk.listStudyGrants({ studyId: target.id, ...params }))
      : sdkCall(sdk.listDtaGrants({ dtaId: target.id, ...params }))
  },

  listDtaGrantCandidates: async (
    dtaId: string,
    params: { limit?: number; nextToken?: string } = {},
  ): Promise<VendorUserListResponse> => {
    return sdkCall(sdk.listDtaGrantCandidates({ dtaId, ...params }))
  },

  revokeGrant: async (
    target: GrantTarget,
    principalId: string,
    capabilitySet: string,
  ): Promise<void> => {
    await (target.type === "study"
      ? sdkCall(sdk.revokeStudyGrant({ studyId: target.id, principalId, capabilitySet }))
      : sdkCall(sdk.revokeDtaGrant({ dtaId: target.id, principalId, capabilitySet })))
  },
}
